// Helpers pra abstrair shape mismatch motor vs Platform legacy.
// Motor (PR #8+) entrega: src, dst, call_start, call_end, agent_name
// Platform legacy esperava: caller, extension, started_at, ended_at
// Estes helpers retornam o melhor disponivel.

import type { VoiceCdr } from './types';

/** Rótulos PT-BR das disposições de chamada (usado em tabela, filtros e CSV).
 * Vocabulário = motor (VoiceCdr::Dispositions). MISSED saiu: o motor nunca
 * gravou esse valor — o filtro "Perdida" retornava sempre vazio (auditoria 22/07). */
export const DISPOSITION_LABEL: Record<string, string> = {
  ANSWERED: 'Atendida',
  NO_ANSWER: 'Sem resposta',
  BUSY: 'Ocupada',
  FAILED: 'Falhou',
  ABANDONED: 'Abandonada (fila)',
  BLOCKED: 'Bloqueada',
  RINGING: 'Em andamento',
};

/** Quem ligou (number/ramal de origem) */
export function getCaller(cdr: VoiceCdr): string {
  return cdr.src ?? cdr.caller ?? '—';
}

/** Pra quem ligou (number/ramal de destino) */
export function getCallee(cdr: VoiceCdr): string {
  return cdr.dst ?? cdr.extension ?? '—';
}

/** Ramal do agente envolvido na call (quem atende) */
export function getAgentExtension(cdr: VoiceCdr): string | null {
  // Motor 09/06 popula answered_by_extension quando AMI AgentConnect chega —
  // fonte de verdade do ramal que atendeu, principalmente no inbound onde
  // dst costuma ser "s" (contexto da URA).
  if (cdr.answered_by_extension) return cdr.answered_by_extension;
  if (cdr.direction === 'inbound') {
    return cdr.dst ?? cdr.extension ?? null;
  }
  return cdr.src ?? cdr.extension ?? null;
}

/** Numero "outro lado" — quem nao e o agente */
export function getRemoteParty(cdr: VoiceCdr): string {
  if (cdr.direction === 'inbound') {
    return cdr.src ?? cdr.caller ?? '—';
  }
  return cdr.dst ?? '—';
}

/** Nome do atendente (se motor populou via assignee) */
export function getAgentName(cdr: VoiceCdr): string | null {
  return cdr.agent_name ?? null;
}

/**
 * Ramal interno pra exibição. Ramal é numérico curto (ex 1001). No inbound o
 * dst costuma ser o contexto da URA ("s") e no outbound o caller id pode ser o
 * número externo — nesses casos não é ramal, retorna null (UI mostra "—").
 */
export function getRamalDisplay(cdr: VoiceCdr): string | null {
  const ext = getAgentExtension(cdr);
  if (ext && /^\d{1,6}$/.test(ext)) return ext;
  return null;
}

/** Inicio da call (Date string) */
export function getCallStart(cdr: VoiceCdr): string | null {
  return cdr.call_start ?? cdr.started_at ?? null;
}

/** Fim da call (Date string ou null se ativa) */
export function getCallEnd(cdr: VoiceCdr): string | null {
  return cdr.call_end ?? cdr.ended_at ?? null;
}

/** Tem gravacao disponivel pra download/play */
export function hasRecording(cdr: VoiceCdr): boolean {
  if (cdr.recording_available) return true;
  return !!cdr.recording_path;
}

/**
 * Extrai o número do protocolo digitado no filtro do histórico.
 * Aceita o protocolo completo ("GKHUB202670"), só os dígitos ("202670") ou
 * direto o número da conversa ("70"). Regra: remove tudo que não é dígito;
 * se sobrar 5+ dígitos começando com ano plausível (20xx), descarta o ano.
 * Retorna null quando não há dígito nenhum.
 */
export function parseProtocoloInput(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const body = digits.length >= 5 && /^20\d\d/.test(digits) ? digits.slice(4) : digits;
  const n = Number(body);
  return Number.isInteger(n) && n > 0 ? n : null;
}
